/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';

const POSITION_TRANSITION_DURATION = 0.5;
const POSITION_VIEWPORT_MAX_HEIGHT_RATIO = 0.5;
const POSITION_VIEWPORT_MAX_WIDTH_RATIO = 0.6;
const READONLY_TRANSFORM_STATE_MAX_AGE = 10_000;
const readonlyTransformStates = new Map();

export const PositionManagerMethods = {
  _onPositionManagerDown() {
    const pos = this._stage.getPointerPosition();
    if (!pos) return;
    this._positionDragging = true;
    this._positionDragStart = { x: pos.x, y: pos.y };
    this._positionStartPos = { x: this.positionPosXValue, y: this.positionPosYValue };
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'grabbing';
  },

  _onPositionManagerMove() {
    if (!this._positionDragStart || !this._positionStartPos) return;
    const pos = this._stage.getPointerPosition();
    if (!pos) return;

    const scale = this._positionManagerScale();
    const refW = this._imgWidth * scale;
    const refH = this._imgHeight * scale;
    let dx = pos.x - this._positionDragStart.x;
    let dy = pos.y - this._positionDragStart.y;
    if (this.workspaceValue) ({ x: dx, y: dy } = this._monitorDragDelta(dx, dy));

    this.positionPosXValue = this._positionStartPos.x + (refW > 0 ? (dx / refW) * 100 : 0);
    this.positionPosYValue = this._positionStartPos.y + (refH > 0 ? (dy / refH) * 100 : 0);
  },

  _onPositionManagerUp() {
    if (!this._positionDragging) return;
    this._positionDragging = false;
    this._positionDragStart = null;
    this._positionStartPos = null;
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'grab';

    this.positionPosXValue = Math.round(this.positionPosXValue);
    this.positionPosYValue = Math.round(this.positionPosYValue);
    this._savePosition();
  },

  _syncPositionManagerMode(active) {
    if (this._positionManagerActive === active) {
      this._syncPositionManagerUi();
      if (active) this._applyPositionManagerTransform();
      return;
    }

    this._positionManagerActive = active;

    if (active) {
      this._deselectShape();
      this._closeEditModal();
      this._editorViewBeforePosition = this._stage
        ? {
            width: this._stage.width(),
            height: this._stage.height(),
            scale: this._currentScale,
            x: this._stage.x(),
            y: this._stage.y()
          }
        : null;
      this._applyPositionWindowDimensions();
      this._syncPositionManagerUi();
      this._applyPositionManagerTransform();
    } else {
      this._positionDragging = false;
      this._restoreEditorViewAfterPosition();
      this._syncPositionManagerUi();
    }
  },

  _syncPositionManagerUi() {
    const active = this._positionManagerActive;
    if (this.workspaceValue) {
      if (this.hasPositionMonitorTarget) this.positionMonitorTarget.classList.toggle('d-none', !active);
      this._syncWorkspaceControls();
      return;
    }
    if (this.hasPositionViewportTarget) this.positionViewportTarget.classList.toggle('d-none', !active);
    if (this.hasBottomZoomControlsTarget) this.bottomZoomControlsTarget.classList.toggle('d-none', active);
    if (this.hasBottomToolbarDividerTarget) this.bottomToolbarDividerTarget.classList.toggle('d-none', active);
    if (this.hasSelectBtnTarget) this.selectBtnTarget.classList.toggle('d-none', active);
    if (this.hasPositionBottomHintTarget) this.positionBottomHintTarget.classList.toggle('d-none', !active);
    if (this.hasDragBtnTarget) {
      this.dragBtnTarget.classList.toggle('active', active || this._tool === 'drag');
      this.dragBtnTarget.setAttribute('aria-pressed', active || this._tool === 'drag' ? 'true' : 'false');
    }
  },

  _restoreEditorViewAfterPosition() {
    if (!this._stage || !this._editorViewBeforePosition) return;
    const view = this._editorViewBeforePosition;
    if (this.workspaceValue) this._stage.rotation(0);
    const width = this.viewportTarget.clientWidth || view.width;
    const height = this.viewportTarget.clientHeight || view.height;
    this._stage.width(width);
    this._stage.height(height);
    this._currentScale = view.scale;
    this._stage.scaleX(view.scale);
    this._stage.scaleY(view.scale);
    this._stage.x(view.x + (width - view.width) / 2);
    this._stage.y(view.y + (height - view.height) / 2);
    this._stage.batchDraw();
    this._updateZoomDisplay();
    this._editorViewBeforePosition = null;
  },

  _handleViewportResize() {
    if (!this._stage || !this.hasViewportTarget) return;
    if (this._positionManagerActive || this.readonlyValue) {
      this._sizePositionViewport();
      this._applyPositionManagerTransform();
    } else {
      const width = this.viewportTarget.clientWidth;
      const height = this.viewportTarget.clientHeight;
      if (width <= 0 || height <= 0) return;
      if (width === this._stage.width() && height === this._stage.height()) return;
      this._cancelCanvasGesture?.();
      this._stage.x(this._stage.x() + (width - this._stage.width()) / 2);
      this._stage.y(this._stage.y() + (height - this._stage.height()) / 2);
      this._stage.size({ width, height });
      this._stage.batchDraw();
      this._rememberEditorState?.();
    }
  },

  _positionBaseScale() {
    if (this.workspaceValue) return this._monitorLayout().base;
    const windowDimensions = this._effectivePositionWindowDimensions();
    const referenceHeight = this.hasPositionViewportTarget
      ? this.positionViewportTarget.getBoundingClientRect().height
      : this.viewportTarget.clientHeight || this._stage?.height() || windowDimensions.height;
    const windowHeight = windowDimensions.height || referenceHeight;

    return referenceHeight > 0 && windowHeight > 0 ? referenceHeight / windowHeight : 1;
  },

  _sizePositionViewport() {
    if (this.workspaceValue) return this._layoutMonitor();
    if (!this.hasPositionViewportTarget || !this.hasViewportTarget) return;

    const { width, height } = this._effectivePositionWindowDimensions();
    const viewportWidth = this.viewportTarget.clientWidth;
    const viewportHeight = this.viewportTarget.clientHeight;
    if (width <= 0 || height <= 0 || viewportWidth <= 0 || viewportHeight <= 0) return;

    const previewScale = Math.min(
      (viewportWidth * POSITION_VIEWPORT_MAX_WIDTH_RATIO) / width,
      (viewportHeight * POSITION_VIEWPORT_MAX_HEIGHT_RATIO) / height
    );

    this.positionViewportTarget.style.width = `${width * previewScale}px`;
    this.positionViewportTarget.style.height = `${height * previewScale}px`;
  },

  _effectivePositionWindowDimensions() {
    const width = this.positionWindowWidthValue || 1920;
    const height = this.positionWindowHeightValue || 1080;

    if (this.gameViewRotationValue === 'ninety' || this.gameViewRotationValue === 'two_seventy') {
      return { width: height, height: width };
    }

    return { width, height };
  },

  _positionManagerScale() {
    return this._positionBaseScale() * (1 + this.positionZoomValue / 100);
  },

  _positionManagerTargetTransform() {
    if (!this._stage || !this.hasViewportTarget) return;
    if (this.workspaceValue) return this._monitorTransform();
    const vw = this.viewportTarget.clientWidth || this._stage.width();
    const vh = this.viewportTarget.clientHeight || this._stage.height();
    const scale = this._positionManagerScale();

    return {
      width: vw,
      height: vh,
      scale,
      x: (vw - this._imgWidth * scale) / 2 + (this.positionPosXValue / 100) * this._imgWidth * scale,
      y: (vh - this._imgHeight * scale) / 2 + (this.positionPosYValue / 100) * this._imgHeight * scale
    };
  },

  _applyPositionManagerTransform({ animate = false, useStoredStart = false } = {}) {
    if (!this._stage || !this.hasViewportTarget) return;
    const target = this._positionManagerTargetTransform();
    if (!target) return;

    this._stage.width(target.width);
    this._stage.height(target.height);
    if (this.workspaceValue) this._stage.rotation(target.rotation || 0);
    this._currentScale = target.scale;

    if (animate && useStoredStart) {
      this._restoreReadonlyPositionTransformStart(target);
      this._syncFogTextureTransform();
    }

    this._positionTransformTween?.destroy();
    this._positionTransformTween = null;

    if (animate && this._canAnimatePositionTransform(target)) {
      let tween = null;
      tween = new Konva.Tween({
        node: this._stage,
        duration: POSITION_TRANSITION_DURATION,
        easing: Konva.Easings.EaseInOut,
        scaleX: target.scale,
        scaleY: target.scale,
        x: target.x,
        y: target.y,
        onUpdate: () => this._syncFogTextureTransform(),
        onFinish: () => {
          this._syncFogTextureTransform();
          if (this._positionTransformTween === tween) this._positionTransformTween = null;
          tween.destroy();
        }
      });
      this._positionTransformTween = tween;
      tween.play();
    } else {
      this._stage.scaleX(target.scale);
      this._stage.scaleY(target.scale);
      this._stage.x(target.x);
      this._stage.y(target.y);
      this._syncFogTextureTransform();
      this._stage.batchDraw();
    }

    this._syncPositionManagerControls();
    if (this.workspaceValue) this._updateZoomDisplay();
  },

  _canAnimatePositionTransform(target) {
    const values = [target.scale, target.x, target.y, this._stage.scaleX(), this._stage.x(), this._stage.y()];
    if (!values.every(Number.isFinite)) return false;

    return (
      Math.abs(this._stage.scaleX() - target.scale) > 0.001 ||
      Math.abs(this._stage.x() - target.x) > 0.5 ||
      Math.abs(this._stage.y() - target.y) > 0.5
    );
  },

  _readonlyPositionTransformStateKey() {
    return this.imageUrlValue || this.element.id || 'konva-map';
  },

  _rememberReadonlyPositionTransform() {
    if (!this.readonlyValue || !this._stage) return;

    readonlyTransformStates.set(this._readonlyPositionTransformStateKey(), {
      at: Date.now(),
      width: this._stage.width(),
      height: this._stage.height(),
      scale: this._stage.scaleX(),
      x: this._stage.x(),
      y: this._stage.y()
    });
  },

  _restoreReadonlyPositionTransformStart(target) {
    if (!this.readonlyValue) return;

    const key = this._readonlyPositionTransformStateKey();
    const previous = readonlyTransformStates.get(key);
    readonlyTransformStates.delete(key);

    if (!previous || Date.now() - previous.at > READONLY_TRANSFORM_STATE_MAX_AGE) return;
    if (Math.abs(previous.width - target.width) > 2 || Math.abs(previous.height - target.height) > 2) return;

    this._stage.scaleX(previous.scale);
    this._stage.scaleY(previous.scale);
    this._stage.x(previous.x);
    this._stage.y(previous.y);
    this._stage.batchDraw();
  },

  _scheduleReadonlyPositionTransform() {
    if (!this.readonlyValue) return;
    if (this._readonlyPositionAnimationFrame) window.cancelAnimationFrame(this._readonlyPositionAnimationFrame);

    this._readonlyPositionAnimationFrame = window.requestAnimationFrame(() => {
      this._readonlyPositionAnimationFrame = null;
      this._applyPositionManagerTransform({ animate: true });
    });
  },

  _applyPositionWindowDimensions() {
    if (this.workspaceValue) return this._layoutMonitor();
    const { width, height } = this._effectivePositionWindowDimensions();
    const placement = this.effectiveCharacterPlacementValue || this.characterPlacementValue || 'top';
    const sidePlacement = ['left', 'right'].includes(placement);
    const charactersWidth = this.positionCharactersWidthValue || width;
    const charactersHeight = this.positionCharactersHeightValue || height * 0.2;
    const characterLongEdge = Math.max(charactersWidth, charactersHeight);
    const characterShortEdge = Math.min(charactersWidth, charactersHeight);
    const previewCharactersWidth = sidePlacement ? characterShortEdge : characterLongEdge;
    const previewCharactersHeight = sidePlacement ? characterLongEdge : characterShortEdge;
    const characterWidthRatio = width > 0 ? previewCharactersWidth / width : 1;
    const characterHeightRatio = height > 0 ? previewCharactersHeight / height : 0.2;

    if (this.hasPositionViewportTarget) {
      this.positionViewportTarget.style.setProperty('--pm-window-ratio', `${width} / ${height}`);
      this.positionViewportTarget.style.setProperty('--pm-character-width-ratio', characterWidthRatio);
      this.positionViewportTarget.style.setProperty('--pm-character-height-ratio', characterHeightRatio);
      this.positionViewportTarget.dataset.pmCharacterPlacement = placement;
      this._sizePositionViewport();
    }
    this.positionViewportLabelTargets.forEach((label) => {
      label.textContent = `${width} × ${height} — player viewport`;
    });
  },

  _syncPositionManagerControls() {
    const zoom = Math.round(this.positionZoomValue);
    const posX = Math.round(this.positionPosXValue);
    const posY = Math.round(this.positionPosYValue);

    if (this.hasPositionZoomInputTarget && document.activeElement !== this.positionZoomInputTarget)
      this.positionZoomInputTarget.value = zoom;
    if (this.hasPositionPosXInputTarget && document.activeElement !== this.positionPosXInputTarget)
      this.positionPosXInputTarget.value = posX;
    if (this.hasPositionPosYInputTarget && document.activeElement !== this.positionPosYInputTarget)
      this.positionPosYInputTarget.value = posY;
    if (this.hasPositionPosXLabelTarget) this.positionPosXLabelTarget.textContent = `X: ${posX}`;
    if (this.hasPositionPosYLabelTarget) this.positionPosYLabelTarget.textContent = `Y: ${posY}`;
  },

  _positionSnapshot() {
    return JSON.stringify({
      game_map: {
        image_zoom: Math.round(this.positionZoomValue),
        image_position_x: Math.round(this.positionPosXValue),
        image_position_y: Math.round(this.positionPosYValue)
      }
    });
  },

  _savePosition({ debounce = false } = {}) {
    if (this.readonlyValue || !this.hasPositionUpdateUrlValue) return;
    this._pendingPositionJson = this._positionSnapshot();
    this._trackMapEdit?.('position', this._pendingPositionJson);
    const unchanged = this._clearSavedMapDraft?.('position');
    this._setSaveState('position', unchanged ? 'saved' : this._autosaveEnabled === false ? 'dirty' : 'saving');
    clearTimeout(this._positionSaveDebounce);
    if (unchanged || this._autosaveEnabled === false) return;
    if (debounce) {
      this._positionSaveDebounce = setTimeout(() => this._flushPositionSave(), 400);
      return;
    }
    this._flushPositionSave();
  },

  _flushPositionSave() {
    if (this._savingPositionJson || !this._pendingPositionJson) return;
    const payload = this._pendingPositionJson;
    const editVersion = this._mapEdits?.position?.version;
    this._pendingPositionJson = null;
    this._savingPositionJson = payload;
    this._failedPositionJson = null;
    this._setSaveState('position', 'saving');

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.positionUpdateUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-CSRF-Token': token
      },
      body: payload
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Position save failed with status ${response.status}`);
        this._acknowledgeMapEdits?.('position', payload, editVersion);
      })
      .catch((err) => {
        this._failedPositionJson = payload;
        console.error('Position save failed:', err);
      })
      .finally(() => {
        this._savingPositionJson = null;
        this._clearSavedMapDraft?.('position');
        if (this._pendingPositionJson && this._autosaveEnabled !== false) this._flushPositionSave();
        else
          this._setSaveState(
            'position',
            this._failedPositionJson ? 'failed' : this._pendingPositionJson ? 'dirty' : 'saved'
          );
      });
  }
};
